/**
 * Epistemic status and uncertainty presentation.
 *
 * Two product rules meet here. A figure must declare what kind of thing it is — an observed
 * source fact, a deterministic calculation, or a model judgment (P-5) — and it must be
 * distinguishable without relying on colour. And a probability is never shown as a bare point:
 * it carries its horizon and says plainly that it is not calibrated (P-10).
 */

export type EpistemicKind = "source_fact" | "calculation" | "judgment" | "stale";

const KIND_LABEL: Record<EpistemicKind, string> = {
  source_fact: "SOURCE FACT",
  calculation: "CALCULATED",
  judgment: "JUDGMENT",
  stale: "STALE",
};

/** A distinct leading glyph per kind, so the four remain tellable apart in greyscale. */
const KIND_MARK: Record<EpistemicKind, string> = {
  source_fact: "◆",
  calculation: "=",
  judgment: "~",
  stale: "!",
};

const KIND_TITLE: Record<EpistemicKind, string> = {
  source_fact: "Observed from a cited source.",
  calculation: "Computed from the saved inputs using a repeatable calculation.",
  judgment: "A model or analyst judgment, not an observation.",
  stale: "Outside its freshness window; not current.",
};

/** Maps a stored probability source to its epistemic kind. */
export function epistemicKindFor(sourceType: string | undefined): EpistemicKind {
  if (sourceType === undefined) return "judgment";
  if (sourceType === "analyst" || sourceType === "model" || sourceType === "reassessment") {
    return "judgment";
  }
  if (sourceType === "calculation" || sourceType === "deterministic") return "calculation";
  if (sourceType === "stale") return "stale";
  return "source_fact";
}

export function EpistemicBadge({ kind }: { kind: EpistemicKind }) {
  return (
    <span className={`epistemic epistemic-${kind}`} title={KIND_TITLE[kind]}>
      <span aria-hidden="true">{KIND_MARK[kind]}</span> {KIND_LABEL[kind]}
    </span>
  );
}

export interface UncertainProbabilityProps {
  readonly percent: number | undefined;
  readonly horizonDays?: number | undefined;
  /** For a horizon counted in trading sessions, as a recorded judgment's is. */
  readonly horizonSessions?: number | undefined;
  readonly sourceType?: string | undefined;
  readonly asOf?: string | undefined;
}

/**
 * A probability with its horizon and calibration status. Where no horizon is recorded the figure
 * says so rather than implying one, because an unbounded probability cannot be acted on.
 */
export function UncertainProbability({
  percent,
  horizonDays,
  horizonSessions,
  sourceType,
  asOf,
}: UncertainProbabilityProps) {
  if (percent === undefined) {
    return <span className="probability-none">No probability assessed</span>;
  }
  const kind = epistemicKindFor(sourceType);
  return (
    <span className="probability">
      <strong>{percent}%</strong>{" "}
      <span className="probability-horizon">
        {horizonSessions !== undefined
          ? `over ${horizonSessions} trading sessions`
          : horizonDays === undefined
            ? "no horizon recorded"
            : `over ${horizonDays} days`}
      </span>{" "}
      <EpistemicBadge kind={kind} />
      <span className="probability-calibration">
        Not calibrated · accuracy has not been verified against past outcomes
      </span>
      {asOf === undefined ? null : (
        <span className="probability-asof">as of {new Date(asOf).toLocaleDateString()}</span>
      )}
    </span>
  );
}
